import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskRevealInView } from "../components/Reveal";
import { IMAGES, CONTACT, ABOUT_PARAS, CREDENTIALS } from "../data";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <>
      <section className="pt-36 md:pt-48 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <p className="font-sans text-[11px] tracking-[0.4em] uppercase text-rosedeep mb-10">
              About — {CONTACT.location}
            </p>
          </Reveal>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.98] text-ink tracking-tight">
            <MaskRevealInView lines={["Hi, I'm"]} />
            <span className="italic font-display text-rosedeep">
              <MaskRevealInView lines={["Roisin."]} delay={0.15} />
            </span>
          </h1>
        </div>
      </section>

      <section ref={ref} className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative overflow-hidden bg-blush aspect-[4/5]">
              <motion.img
                src={IMAGES.about}
                alt="Roisin Doyle"
                style={{ y }}
                className="absolute inset-[-8%] h-[116%] w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-8 max-w-2xl">
              {ABOUT_PARAS.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <p className={`font-sans leading-relaxed ${i === 0 ? "text-xl md:text-2xl font-light text-graphite" : "text-base md:text-lg text-mutedfg"}`}>
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl text-ink mt-16 mb-8">Why work with me</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {CREDENTIALS.map((c, i) => (
                <Reveal key={c} delay={(i % 2) * 0.05}>
                  <div className="flex items-start gap-3 border-t border-borderwarm pt-4">
                    <Check size={16} className="mt-1.5 shrink-0 text-rosedeep" />
                    <p className="font-sans text-base leading-snug text-graphite">{c}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <Link
                to="/contact"
                data-testid="about-cta"
                className="group inline-flex items-center gap-3 mt-14 rounded-full bg-rosedeep text-white px-8 py-4 font-sans text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 hover:bg-ink"
              >
                Work With Me
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
